import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Users, 
  Shield, 
  TrendingUp,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Check,
  AlertTriangle,
  X
} from 'lucide-react';

const QUESTIONS = [
  {
    id: 'partners',
    question: '¿Cuántas personas formarán la empresa?',
    icon: Users,
    options: [
      { value: '1', label: 'Solo yo', emoji: '👤' },
      { value: '2-5', label: '2 a 5 socios', emoji: '👥' },
      { value: '6-50', label: '6 a 50 socios', emoji: '👨‍👩‍👧‍👦' },
      { value: '50+', label: 'Más de 50', emoji: '🏢' }
    ]
  },
  {
    id: 'liability',
    question: '¿Qué nivel de riesgo personal aceptas?',
    icon: Shield,
    options: [
      { value: 'limited', label: 'Solo arriesgo mi aporte', emoji: '🛡️' },
      { value: 'unlimited', label: 'Acepto riesgo total', emoji: '⚠️' }
    ]
  },
  {
    id: 'investment',
    question: '¿Necesitarás inversionistas externos?',
    icon: TrendingUp,
    options: [
      { value: 'no', label: 'No, capital propio', emoji: '💰' },
      { value: 'maybe', label: 'Quizás en el futuro', emoji: '📈' },
      { value: 'yes', label: 'Sí, busco inversión', emoji: '🚀' }
    ]
  },
  {
    id: 'complexity',
    question: '¿Qué prefieres para tu empresa?',
    icon: Building2,
    options: [
      { value: 'simple', label: 'Simple y económica', emoji: '✨' },
      { value: 'flexible', label: 'Flexible y moderna', emoji: '🔄' },
      { value: 'formal', label: 'Formal y estructurada', emoji: '📋' }
    ]
  }
];

const COMPANY_RECOMMENDATIONS = {
  'constitucion_eirl': {
    name: 'EIRL',
    fullName: 'Empresa Individual de Responsabilidad Limitada',
    description: 'Ideal para emprendedores individuales',
    color: 'blue',
    advantages: ['Un solo dueño', 'Responsabilidad limitada', 'Trámite simple']
  },
  'constitucion_spa': {
    name: 'SpA',
    fullName: 'Sociedad por Acciones',
    description: 'La favorita de startups e inversionistas',
    color: 'purple',
    advantages: ['Máxima flexibilidad', 'Fácil recibir inversión', 'Puede tener 1 accionista']
  },
  'constitucion_ltda': {
    name: 'Ltda',
    fullName: 'Sociedad de Responsabilidad Limitada',
    description: 'Clásica para negocios familiares y PyMEs',
    color: 'green',
    advantages: ['Responsabilidad limitada', 'Estructura simple', 'Bajo costo']
  },
  'constitucion_sa_cerrada': {
    name: 'S.A. Cerrada',
    fullName: 'Sociedad Anónima Cerrada',
    description: 'Para empresas medianas con estructura formal',
    color: 'amber',
    advantages: ['Acciones transferibles', 'Directorio formal', 'Mayor credibilidad']
  },
  'constitucion_sa_abierta': {
    name: 'S.A. Abierta',
    fullName: 'Sociedad Anónima Abierta',
    description: 'Para cotizar en bolsa',
    color: 'red',
    advantages: ['Acceso a mercado de valores', 'Máximo financiamiento', 'Prestigio']
  },
  'constitucion_colectiva': {
    name: 'Colectiva',
    fullName: 'Sociedad Colectiva Comercial',
    description: 'Para socios que quieren gestión compartida',
    color: 'orange',
    advantages: ['Gestión entre todos', 'Ideal para profesionales'],
    warning: 'Responsabilidad ilimitada'
  },
  'constitucion_cooperativa': {
    name: 'Cooperativa',
    fullName: 'Cooperativa',
    description: 'Enfoque social y comunitario',
    color: 'teal',
    advantages: ['Beneficios tributarios', 'Un socio = un voto', 'Apoyo estatal']
  }
};

function getRecommendation(answers) {
  const { partners, liability, investment, complexity } = answers;
  
  // Solo 1 persona
  if (partners === '1') {
    if (investment === 'yes' || investment === 'maybe') {
      return ['constitucion_spa', 'constitucion_eirl'];
    }
    return ['constitucion_eirl', 'constitucion_spa'];
  }
  
  // Más de 50 socios o busca bolsa
  if (partners === '50+') {
    return ['constitucion_sa_abierta', 'constitucion_sa_cerrada'];
  }
  
  // Acepta responsabilidad ilimitada (profesionales)
  if (liability === 'unlimited') {
    return ['constitucion_colectiva', 'constitucion_ltda'];
  }
  
  // Busca inversión activamente
  if (investment === 'yes') {
    if (complexity === 'formal') {
      return ['constitucion_sa_cerrada', 'constitucion_spa'];
    }
    return ['constitucion_spa', 'constitucion_sa_cerrada'];
  }
  
  // Prefiere flexibilidad
  if (complexity === 'flexible') {
    return ['constitucion_spa', 'constitucion_ltda'];
  }
  
  // Prefiere estructura formal
  if (complexity === 'formal') {
    return ['constitucion_sa_cerrada', 'constitucion_ltda'];
  }
  
  // Por defecto: simple y económica
  if (partners === '2-5') {
    return ['constitucion_ltda', 'constitucion_spa'];
  }
  
  return ['constitucion_spa', 'constitucion_ltda'];
}

const CompanyTypeWizard = ({ onSelect, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  const currentQuestion = QUESTIONS[step];
  const progress = ((step + 1) / QUESTIONS.length) * 100;

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentQuestion.id]: value };
    setAnswers(newAnswers);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Calculate recommendation
      const recs = getRecommendation(newAnswers);
      setRecommendations(recs);
      setShowResult(true);
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleSelectCompany = (companyId) => {
    // Map old IDs to new company types for the new page
    const typeMap = {
      'constitucion_spa': 'spa',
      'constitucion_ltda': 'ltda',
      'constitucion_eirl': 'eirl',
      'constitucion_sa_cerrada': 'sa_cerrada',
      'constitucion_sa_abierta': 'sa_cerrada', // Redirect to cerrada (abierta requires CMF)
      'constitucion_colectiva': 'ltda', // Fallback to ltda
      'constitucion_cooperativa': 'ltda' // Fallback to ltda
    };
    const type = typeMap[companyId] || 'spa';
    onClose();
    navigate(`/constituir-empresa?tipo=${type}`);
  };

  const handleRestart = () => {
    setStep(0);
    setAnswers({});
    setShowResult(false);
    setRecommendations([]);
  };

  if (showResult) {
    const primary = COMPANY_RECOMMENDATIONS[recommendations[0]];
    const secondary = recommendations[1] ? COMPANY_RECOMMENDATIONS[recommendations[1]] : null;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-6 relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>

          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">¡Te recomendamos!</h3>
            <p className="text-slate-400 text-sm mt-1">Basado en tus respuestas</p>
          </div>

          {/* Primary Recommendation */}
          <div className={`bg-gradient-to-br from-${primary.color}-500/20 to-${primary.color}-600/10 border border-${primary.color}-500/30 rounded-xl p-5 mb-4`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-12 h-12 bg-${primary.color}-500/20 rounded-xl flex items-center justify-center`}>
                <Building2 className={`w-6 h-6 text-${primary.color}-400`} />
              </div>
              <div>
                <h4 className={`text-lg font-bold text-${primary.color}-400`}>{primary.name}</h4>
                <p className="text-xs text-slate-400">{primary.fullName}</p>
              </div>
              <span className="ml-auto px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
                Mejor opción
              </span>
            </div>
            <p className="text-sm text-slate-300 mb-3">{primary.description}</p>
            <div className="flex flex-wrap gap-2">
              {primary.advantages.map((adv, i) => (
                <span key={i} className="px-2 py-1 bg-slate-800/50 text-slate-300 text-xs rounded-lg flex items-center gap-1">
                  <Check className="w-3 h-3 text-emerald-400" />
                  {adv}
                </span>
              ))}
            </div>
            {primary.warning && (
              <div className="mt-3 flex items-center gap-2 text-amber-400 text-xs">
                <AlertTriangle className="w-4 h-4" />
                {primary.warning}
              </div>
            )}
            <button
              onClick={() => handleSelectCompany(recommendations[0])}
              className={`w-full mt-4 py-2.5 bg-${primary.color}-600 hover:bg-${primary.color}-700 rounded-lg font-medium transition-colors flex items-center justify-center gap-2`}
            >
              Usar esta plantilla
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Secondary Recommendation */}
          {secondary && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-white">{secondary.name}</h4>
                  <p className="text-xs text-slate-400">{secondary.description}</p>
                </div>
                <button
                  onClick={() => handleSelectCompany(recommendations[1])}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
                >
                  Elegir
                </button>
              </div>
            </div>
          )}

          <button
            onClick={handleRestart}
            className="w-full mt-4 py-2 text-slate-400 hover:text-white text-sm transition-colors"
          >
            Volver a empezar
          </button>
        </div>
      </div>
    );
  }

  const Icon = currentQuestion.icon;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 relative">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-slate-800 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
            <span>Pregunta {step + 1} de {QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-green-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon className="w-7 h-7 text-emerald-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">{currentQuestion.question}</h3>
        </div>

        {/* Options */}
        <div className="space-y-2">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleAnswer(option.value)}
              className={`w-full p-4 rounded-xl border transition-all text-left flex items-center gap-3 ${
                answers[currentQuestion.id] === option.value
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-white'
                  : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800 hover:border-slate-600'
              }`}
            >
              <span className="text-2xl">{option.emoji}</span>
              <span className="font-medium">{option.label}</span>
              <ChevronRight className="w-5 h-5 ml-auto text-slate-500" />
            </button>
          ))}
        </div>

        {/* Back button */}
        {step > 0 && (
          <button
            onClick={handleBack}
            className="mt-4 w-full py-2 text-slate-400 hover:text-white text-sm transition-colors flex items-center justify-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Pregunta anterior
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanyTypeWizard;
